import React from 'react';
import { Settings, Code, ExternalLink, CheckCircle, XCircle, PlusCircle, Puzzle, SlidersHorizontal } from 'lucide-react';
import { AgentData_V10, AgentHubPageViewMode, IntegrationShowcaseItemV10 } from '@dopomogai/types';
import { Card } from '@dopomogai/ui';
import {Button} from '@dopomogai/ui';
import { Badge } from '@dopomogai/ui';
import { useTranslation } from 'react-i18next'; // Import useTranslation
import clsx from 'clsx'; // Import clsx for cleaner class management

interface IntegrationsSectionProps {
  agentData: AgentData_V10;
  viewMode: AgentHubPageViewMode;
  onAction: (actionType: string, payload?: any) => void;
  headerBgColor?: string;
  id?: string;
}

interface IntegrationCardProps {
  integration: IntegrationShowcaseItemV10;
  isOwnerView: boolean;
  onAction: (actionType: string, payload?: any) => void;
}

const IntegrationDisplayCard: React.FC<IntegrationCardProps> = ({
  integration,
  isOwnerView,
  onAction,
}) => {
  const isClickable = (isOwnerView && integration.configUrl) || integration.url;

  const handleCardClick = () => {
    if (isOwnerView && integration.configUrl) {
      onAction('CONFIGURE_INTEGRATION', { integrationId: integration.id, url: integration.configUrl });
    } else if (integration.url) { // Public link for more info
      window.open(integration.url, '_blank');
    }
  };

  const handleActionClick = (e: React.MouseEvent) => {
    e.stopPropagation(); // Prevent card click when button is clicked
    if (isOwnerView) {
        if (integration.isConnected) {
            onAction('DISCONNECT_INTEGRATION', { integrationId: integration.id });
        } else {
            onAction('CONNECT_INTEGRATION', { integrationId: integration.id });
        }
    } else if (integration.isOneClickSetup) {
        onAction('SETUP_INTEGRATION', { integrationId: integration.id });
    }
  };

  return (
    <div
      className={clsx(`
        bg-white border-2 rounded-xl p-4 flex flex-col items-center justify-between h-full
        transform transition-all duration-200 hover:-translate-y-1 hover:shadow-[4px_4px_0_0_rgb(17,24,39)]
      `,
        isClickable ? 'cursor-pointer' : 'cursor-default',
        isOwnerView && integration.isConnected ? 'border-emerald-400 bg-emerald-50/50' : 'border-black'
      )}
      onClick={isClickable ? handleCardClick : undefined}
      title={integration.description || integration.name}
    >
      <div className="w-16 h-16 flex items-center justify-center mb-3 p-1 bg-white rounded-full border-2 border-black overflow-hidden">
        <img
          src={integration.logoUrl}
          alt={`${integration.name} logo`}
          className="max-w-full max-h-full object-contain"
        />
      </div>
      <p className="text-sm font-semibold text-center text-gray-800 mb-1 leading-tight">
        {integration.name}
      </p>
      <p className="text-xs text-gray-500 text-center mb-3 h-8 overflow-hidden">
        {integration.type}
      </p>

      {isOwnerView ? (
        <Button
          text={integration.isConnected ? "Disconnect" : "Connect"}
          variant={integration.isConnected ? "outline" : "primary"}
          size="sm"
          icon={integration.isConnected ? <XCircle size={14} /> : <PlusCircle size={14} />}
          onClick={handleActionClick}
          className="w-full mt-auto"
          ariaLabel={`${integration.isConnected ? "Disconnect" : "Connect"} ${integration.name}`}
        />
      ) : integration.isOneClickSetup ? (
        <Button
          text="One-Click Setup"
          variant="primary"
          size="sm"
          onClick={handleActionClick}
          className="w-full mt-auto"
          ariaLabel={`Setup ${integration.name} with one click`}
        />
      ) : integration.url && (
        <Button
          text="Learn More"
          variant="ghost"
          size="sm"
          icon={<ExternalLink size={12}/>}
          iconPosition='right'
          onClick={(e) => { e.stopPropagation(); window.open(integration.url, '_blank');}}
          className="w-full mt-auto text-xs"
          ariaLabel={`Learn more about ${integration.name}`}
        />
      )}
    </div>
  );
};


export const IntegrationsSection = React.forwardRef<HTMLDivElement, IntegrationsSectionProps>(
  ({
  agentData,
  viewMode,
  onAction,
  headerBgColor,
  id
}, ref) => {
  const { t } = useTranslation('agent-common'); // Hook for i18n
  const isOwnerView = viewMode === 'owner_dashboard';
  const { integrationsSection, developerAccessOwner, customWidgetOptionsOwner, dedicatedInterfaces, voiceConfiguration, threeDAvatarConfiguration } = agentData;

  if (!integrationsSection && !isOwnerView) return null;

  return (
    <Card
      id={id}
      ref={ref}
      title={integrationsSection?.title || t('integrationsSection.defaultTitle', "Integrations Hub")}
      headerBgColor={headerBgColor}
      icon={<Puzzle size={22} className="text-teal-600" />}
      actionButton={
        isOwnerView && integrationsSection?.allIntegrationsManagementUrl && (
          <Button
            text="Manage All"
            variant="outline"
            size="sm"
            icon={<Settings size={16} />}
            onClick={() => onAction('MANAGE_INTEGRATIONS', { url: integrationsSection.allIntegrationsManagementUrl })}
          />
        )
      }
    >
    <div className="space-y-12">
      {/* Featured Integrations */}
      {integrationsSection?.oneClickIntegrations && integrationsSection.oneClickIntegrations.length > 0 && (
        <div>
          <h3 className="text-lg font-semibold text-gray-800 mb-4 pb-2 border-b-2 border-gray-200">
            {t('integrationsSection.featuredIntegrationsTitle', 'Featured Integrations')}
          </h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-6">
            {integrationsSection.oneClickIntegrations.map((integration) => (
              <IntegrationDisplayCard
                key={integration.id}
                integration={integration}
                isOwnerView={isOwnerView}
                onAction={onAction}
              />
            ))}
          </div>
        </div>
      )}

      {/* Other Supported Integrations */}
      {(integrationsSection?.otherSupportedIntegrations && integrationsSection.otherSupportedIntegrations.length > 0) && (
        <div>
          <h3 className="text-lg font-semibold text-gray-800 mb-4 pb-2 border-b-2 border-gray-200">
            {t('integrationsSection.alsoSupportsTitle', 'Also Supports')}
          </h3>
          <div className="grid grid-cols-2 sm:grid-cols-3 md:grid-cols-4 lg:grid-cols-5 gap-4 md:gap-6">
            {integrationsSection.otherSupportedIntegrations.map((integration) => (
              <IntegrationDisplayCard
                key={integration.id}
                integration={integration}
                isOwnerView={isOwnerView}
                onAction={onAction}
              />
            ))}
            {/* === NEW "Request an Integration" Card === */}
            <button
                onClick={() => onAction('REQUEST_INTEGRATION', null)}
                className={clsx(
                    `bg-slate-50 border-2 border-black border-dashed rounded-xl p-4 flex flex-col items-center justify-center h-full
                    text-slate-600 hover:text-black hover:bg-slate-100 hover:border-solid
                    transform transition-all duration-200 hover:-translate-y-1 hover:shadow-[4px_4px_0_0_rgb(17,24,39)]
                    focus:outline-none focus:ring-2 focus:ring-offset-2 focus:ring-emerald-500`
                )}
                aria-label={t('integrationsSection.requestAriaLabel', 'Request a new integration')}
            >
                <PlusCircle size={24} className="mb-2" />
                <p className="text-sm font-semibold text-center leading-tight">
                    {t('integrationsSection.requestTitle', 'Request an Integration')}
                </p>
            </button>
          </div>
        </div>
      )}

      {/* A2A Protocol */}
      {integrationsSection?.a2aProtocolConfig?.isEnabled && (
        <div className="bg-sky-50 border-2 border-black rounded-xl p-5">
          <div className="flex items-start gap-4">
             <div className="p-2 bg-white rounded-lg border-2 border-black shadow-sm mt-0.5">
                <Code size={24} className="text-sky-600" />
            </div>
            <div>
              <h3 className="font-semibold text-sky-800 text-md">{t('integrationsSection.a2aProtocolTitle', 'Agent-to-Agent (A2A) Protocol Supported')}</h3>
              <p className="text-sky-700 text-sm mt-1">
                {t('integrationsSection.a2aProtocolDescription', 'This agent can communicate directly with other compatible AI agents using the A2A protocol.')}
              </p>

              {isOwnerView && integrationsSection.a2aProtocolConfig.manageA2AUrl && (
                <Button
                    text="Manage A2A Settings"
                    variant="secondary"
                    size="sm"
                    icon={<Settings size={14}/>}
                    onClick={() => onAction('MANAGE_A2A_PROTOCOL', { url: integrationsSection.a2aProtocolConfig.manageA2AUrl })}
                    className="mt-3"
                />
              )}
              {isOwnerView && integrationsSection.a2aProtocolConfig.publicKey && (
                 <p className="text-xs text-gray-600 mt-2 font-mono bg-white p-2 border border-gray-300 rounded break-all">
                    Public Key: {integrationsSection.a2aProtocolConfig.publicKey}
                 </p>
              )}
            </div>
          </div>
        </div>
      )}

      {/* --- Owner View Specific Sections --- */}
      {isOwnerView && (
        <div className="mt-10 space-y-8">
          {/* Developer Access & Custom Widget (Side by side) */}
          {(developerAccessOwner || customWidgetOptionsOwner) && (
            <div>
              <h3 className="text-xl font-bold text-gray-800 mb-4 pb-2 border-b-2 border-gray-300">Developer & Embedding Options</h3>
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                {developerAccessOwner && (
                  <div className="bg-slate-50 rounded-xl p-5 border-2 border-black">
                    <h4 className="font-semibold text-gray-800 mb-3 text-md">Developer API & SDK</h4>
                    <div className="space-y-2 mb-4 text-sm">
                      <p>
                        <span className="font-medium">API Status:</span>{' '}
                        <Badge
                            text={developerAccessOwner.apiEnabled ? "Enabled" : "Disabled"}
                            accentColor={developerAccessOwner.apiEnabled ? "green" : "red"}
                        />
                      </p>
                       {developerAccessOwner.apiEnabled && developerAccessOwner.apiKey && (
                        <p className="font-mono text-xs bg-white p-2 border rounded break-all">Key: {developerAccessOwner.apiKey}</p>
                       )}
                      <p><span className="font-medium">SDKs:</span> {developerAccessOwner.sdkLanguagesAvailable.join(', ') || "None"}</p>
                    </div>
                    <div className="flex flex-col sm:flex-row gap-2">
                        <Button text="Manage API/SDK" variant="secondary" size="sm" icon={<Settings size={14}/>} onClick={() => onAction('MANAGE_API_SDK', {url: developerAccessOwner.manageApiSdkUrl})} className="flex-1"/>
                        <Button text="API Docs" variant="ghost" size="sm" icon={<ExternalLink size={12}/>} iconPosition='right' onClick={() => window.open(developerAccessOwner.apiDocumentationUrl, '_blank')} className="flex-1"/>
                        <Button text="SDK Docs" variant="ghost" size="sm" icon={<ExternalLink size={12}/>} iconPosition='right' onClick={() => window.open(developerAccessOwner.sdkDocumentationUrl, '_blank')} className="flex-1"/>
                    </div>
                  </div>
                )}

                {customWidgetOptionsOwner && (
                  <div className="bg-slate-50 rounded-xl p-5 border-2 border-black">
                    <h4 className="font-semibold text-gray-800 mb-3 text-md">Custom Website Widget</h4>
                     <div className="space-y-2 mb-4 text-sm">
                        <p>
                            <span className="font-medium">Widget Status:</span>{' '}
                            <Badge
                                text={customWidgetOptionsOwner.isEnabled ? "Enabled" : "Disabled"}
                                accentColor={customWidgetOptionsOwner.isEnabled ? "green" : "red"}
                            />
                        </p>
                        {customWidgetOptionsOwner.isEnabled && customWidgetOptionsOwner.embedScript && (
                            <div>
                                <p className="font-medium mb-1">Embed Script:</p>
                                <textarea readOnly value={customWidgetOptionsOwner.embedScript} className="w-full text-xs p-2 font-mono bg-white border rounded h-20 resize-none"/>
                            </div>
                        )}
                    </div>
                    <Button text="Manage Widgets" variant="secondary" size="sm" icon={<SlidersHorizontal size={14}/>} onClick={() => onAction('MANAGE_CUSTOM_WIDGETS', {url: customWidgetOptionsOwner.manageCustomWidgetsUrl})} fullWidth/>
                  </div>
                )}
              </div>
            </div>
          )}

          {/* Dedicated Interfaces, Voice, 3D Avatar */}
          {(dedicatedInterfaces && dedicatedInterfaces.length > 0 || voiceConfiguration || threeDAvatarConfiguration) && (
            <div>
              <h3 className="text-xl font-bold text-gray-800 mb-4 pb-2 border-b-2 border-gray-300">Advanced Interface Configuration</h3>
              <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
                {dedicatedInterfaces && dedicatedInterfaces.length > 0 && (
                  <div className="bg-slate-50 rounded-xl p-5 border-2 border-black">
                    <h4 className="font-semibold text-gray-800 mb-2 text-md">Dedicated Interfaces</h4>
                    <ul className="text-sm list-disc list-inside space-y-1 mb-3">
                        {dedicatedInterfaces.slice(0,2).map(di => <li key={di.id}>{di.name} ({di.type})</li>)}
                        {dedicatedInterfaces.length > 2 && <li>+{dedicatedInterfaces.length-2} more...</li>}
                    </ul>
                    <Button text="Manage Interfaces" variant="secondary" size="sm" onClick={() => onAction('MANAGE_DEDICATED_INTERFACES', null)} fullWidth/>
                  </div>
                )}
                {voiceConfiguration && (
                  <div className="bg-slate-50 rounded-xl p-5 border-2 border-black">
                    <h4 className="font-semibold text-gray-800 mb-2 text-md">Voice Configuration</h4>
                    <p className="text-sm text-gray-600 mb-3">Current voice: <span className="font-medium">{voiceConfiguration.currentVoice || "Default"}</span></p>
                    <Button text="Manage Voice" variant="secondary" size="sm" onClick={() => onAction('MANAGE_VOICE', {url: voiceConfiguration.manageVoiceUrl})} fullWidth/>
                  </div>
                )}
                {threeDAvatarConfiguration && (
                  <div className="bg-slate-50 rounded-xl p-5 border-2 border-black">
                    <h4 className="font-semibold text-gray-800 mb-2 text-md">3D Avatar</h4>
                    <p className="text-sm text-gray-600 mb-3">
                      {threeDAvatarConfiguration.currentAvatarModelUrl ? "Custom 3D avatar active" : "Default avatar"}
                    </p>
                    <Button text="Manage 3D Avatar" variant="secondary" size="sm" onClick={() => onAction('MANAGE_3D_AVATAR', {url: threeDAvatarConfiguration.manage3DAvatarUrl})} fullWidth/>
                  </div>
                )}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
    </Card>
  );
});

IntegrationsSection.displayName = 'IntegrationsSection';
